import { environment } from "environments/environment";
import { HttpClient } from "@angular/common/http";
import { catchError, map, tap } from "rxjs/operators";
import { Observable, of } from "rxjs";
import { LRUCache } from 'lru-cache';
import { ApiResponse } from '@type/ApiResponse';
import { parseErrorList, parseErrorMessage } from "@utils";
import { inject } from "@angular/core";

type Options = {
  headers?: Record<string, string>;
  cache?: boolean;
  raw?: boolean;
};

export class ApiClass {

  protected _http = inject(HttpClient);

  protected _apiUrl = environment.apiUrl;
  
  private _cache = new LRUCache({
    max: 100,
    ttl: 1000 * 60 * 5, // 5 minutes
  });
  
  constructor() { }
  
  protected url(path: string, queryParams?: Record<string, any>) {
    if (queryParams) {
      path += '?' + this.buildQueryParams(queryParams);
    }
    return `${this._apiUrl}${path}`;
  }
  
  protected buildQueryParams(params: Record<string, any>) {
    const cleanParams = {};
    for (let key in params) {
      if (params[key]) {
        cleanParams[key] = params[key];
      }
    }
    return new URLSearchParams(cleanParams).toString();
  }
  
  protected buildFormData(params: Record<string, any>) {
    let formData = new FormData();
    for (let key in params) {
      if (params[key]) {
        formData.append(key, params[key]);
      }
    }
    return formData;
  }
  
  protected get<TData = any>(path: `/${string}`, queryParams?: Record<string, any>, reqOptions?: Options) {
    const options = this._parseOptions(reqOptions || {});
    if (reqOptions?.cache) {
      const cacheKey = this.url(path, queryParams);
      const cached = this._cache.get(cacheKey);
      if (cached) {
        return of(cached) as Observable<TData>;
      } else {
        return this._http.get<ApiResponse<TData>>(this.url(path, queryParams), options).pipe(
          this._mapResponse(reqOptions),
          tap(data => {
            this._cache.set(cacheKey, data);
          })
        );
      }
    } else {
      return this._http.get<ApiResponse<TData>>(this.url(path, queryParams), options).pipe(this._mapResponse(reqOptions));
    }
  }
  
  protected post<TData = any>(path: `/${string}`, body: Record<string, any> | FormData, reqOptions?: Options) {
    const options = this._parseOptions(reqOptions || {});
    return this._http.post<ApiResponse<TData>>(this.url(path), body, options).pipe(this._mapResponse(reqOptions));
  }
  
  protected put<TData = any>(path: `/${string}`, body: Record<string, any>, reqOptions?: Options) {
    const options = this._parseOptions(reqOptions || {});
    return this._http.put<ApiResponse<TData>>(this.url(path), body, options).pipe(this._mapResponse(reqOptions));
  }
  
  protected delete<TData = any>(path: `/${string}`, queryParams?: Record<string, any>, reqOptions?: Options) {
    const options = this._parseOptions(reqOptions || {});
    return this._http.delete<ApiResponse<TData>>(this.url(path, queryParams), options).pipe(this._mapResponse(reqOptions));
  }
  
  protected patch<TData = any>(path: `/${string}`, body?: Record<string, any>, reqOptions?: Options) {
    const options = this._parseOptions(reqOptions || {});
    return this._http.patch<ApiResponse<TData>>(this.url(path), body, options).pipe(this._mapResponse(reqOptions));
  }
  
  private _mapResponse<T>(reqOptions?: Options) {
    return (source: Observable<ApiResponse<T>>) => {
      return source.pipe(
        catchError((error) => {
          throw {
            list: parseErrorList(error),
            message: parseErrorMessage(error),
            error,
          };
        }),
        map(project => {
          if(project) {
            if (reqOptions?.raw) {
              return project as unknown as T;
            }
            if (project.ok === true) {
              return project.data;
            } else {
              throw project.errors;
            }
          }
        })
      );
    };
  }
  
  private _parseOptions(options: Options) {
    const headers = {};
    if (options.headers) {
      for (let key in options.headers) {
        headers[key] = options.headers[key];
      }
    }
    return {
      headers
    }
  }
  
}